import { DNDFieldsEnum } from "./Types";
import { golfTooltipHeaders } from "./TooltipInformation";

/** Payload field keys for Golf → Retail Sub Categories (one level per row). */
export const RETAIL_SUB_CATEGORY_FIELD_NUMBER = "Sub Category Number";
export const RETAIL_SUB_CATEGORY_FIELD_NAME = "Sub Category Name";
/** @deprecated Legacy payload key; read when loading saved data. */
export const RETAIL_SUB_CATEGORY_FIELD_VALUE_LEGACY = "Sub Category Value";
export const RETAIL_SUB_CATEGORY_FIELD_ACTIVE = "Active";

export const RETAIL_SUB_CATEGORY_LEVELS = [1, 2, 3, 4, 5] as const;

export type RetailSubCategoryLevel =
  (typeof RETAIL_SUB_CATEGORY_LEVELS)[number];

export const getRetailSubCategoryLevelLabel = (
  level: RetailSubCategoryLevel,
): string => `Level ${level}`;

export const RETAIL_SUB_CATEGORY_LEGACY_KEYS = [
  DNDFieldsEnum.SUBCATEGORY1,
  DNDFieldsEnum.SUBCATEGORY2,
  DNDFieldsEnum.SUBCATEGORY3,
  DNDFieldsEnum.SUBCATEGORY4,
  DNDFieldsEnum.SUBCATEGORY5,
] as const;

export const RETAIL_SUB_CATEGORY_SAMPLE_MARKER = "sample_test_agilysys";

export const RETAIL_SUB_CATEGORY_PAGE_TITLE = "Retail Sub Categories";
export const RETAIL_SUB_CATEGORY_LABEL_NUMBER = "Sub Category Number *";
export const RETAIL_SUB_CATEGORY_LABEL_NAME = "Sub Category Name *";
export const RETAIL_SUB_CATEGORY_LABEL_ACTIVE = "Active";
export const RETAIL_SUB_CATEGORY_SELECT_LEVEL_PLACEHOLDER = "Select level";
export const RETAIL_SUB_CATEGORY_LIST_TITLE = "Retail Sub Categories Available";
export const RETAIL_SUB_CATEGORY_EMPTY_LIST_MESSAGE =
  "No Retail Sub Category Information Available";
export const RETAIL_SUB_CATEGORY_BUTTON_SUFFIX = "Retail Sub Category";
export const RETAIL_SUB_CATEGORY_SAMPLE_WARNING =
  "Do not use sample data. Those are for demo purposes only!";

export const RETAIL_SUB_CATEGORY_TOOLTIPS = {
  header: golfTooltipHeaders.retailSubCategoryHeaders,
  number: golfTooltipHeaders.subCategoryName,
  name: golfTooltipHeaders.subCategoryName,
  active: golfTooltipHeaders.active,
} as const;

export const RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES = {
  numberMandatory: "Sub Category Number is mandatory",
  nameMandatory: "Sub Category Name is mandatory",
  duplicateEntry: "Cannot have duplicate entries",
  duplicateNamesInBatch: "Duplicate Sub Category Names in the list to add",
} as const;

export type RetailSubCategoryPayload = {
  [RETAIL_SUB_CATEGORY_FIELD_NUMBER]: RetailSubCategoryLevel | "";
  [RETAIL_SUB_CATEGORY_FIELD_NAME]: string | null;
  [RETAIL_SUB_CATEGORY_FIELD_ACTIVE]: boolean;
};

export const rowContainsRetailSubCategorySample = (obj: any): boolean =>
  Object.values(obj ?? {}).some((value: any) => {
    if (typeof value === "string") {
      return value.toLowerCase().includes(RETAIL_SUB_CATEGORY_SAMPLE_MARKER);
    }
    if (Array.isArray(value)) {
      return value.some(
        (item) =>
          typeof item === "string" &&
          item.toLowerCase().includes(RETAIL_SUB_CATEGORY_SAMPLE_MARKER),
      );
    }
    return false;
  });

export const isRetailSubCategorySampleValue = (value: unknown): boolean =>
  typeof value === "string" &&
  value.toLowerCase().includes(RETAIL_SUB_CATEGORY_SAMPLE_MARKER);

export const isRetailSubCategoryActive = (
  record: Record<string, unknown>,
): boolean => {
  const active = record[RETAIL_SUB_CATEGORY_FIELD_ACTIVE];
  return active !== false && active !== "No" && active !== "no";
};

export const parseRetailSubCategoryLevelFromRow = (
  list: Record<string, unknown> | null | undefined,
): RetailSubCategoryLevel | "" => {
  const level = Number(list?.[RETAIL_SUB_CATEGORY_FIELD_NUMBER]);
  return RETAIL_SUB_CATEGORY_LEVELS.includes(level as RetailSubCategoryLevel)
    ? (level as RetailSubCategoryLevel)
    : "";
};

export const isRetailSubCategoryRowActive = (
  list: Record<string, unknown> | null | undefined,
): boolean => list?.[RETAIL_SUB_CATEGORY_FIELD_ACTIVE] !== false;

export const getRetailSubCategoryNameFromRow = (
  record: Record<string, unknown> | null | undefined,
): string | null => {
  const name = record?.[RETAIL_SUB_CATEGORY_FIELD_NAME];
  if (name != null && String(name).trim()) {
    return String(name);
  }
  const legacy = record?.[RETAIL_SUB_CATEGORY_FIELD_VALUE_LEGACY];
  return legacy != null ? String(legacy) : null;
};

export const buildRetailSubCategoryPayload = (
  subCategoryNumber: RetailSubCategoryLevel | "",
  subCategoryName: string | null,
  active: boolean,
): RetailSubCategoryPayload => ({
  [RETAIL_SUB_CATEGORY_FIELD_NUMBER]: subCategoryNumber,
  [RETAIL_SUB_CATEGORY_FIELD_NAME]: subCategoryName,
  [RETAIL_SUB_CATEGORY_FIELD_ACTIVE]: active,
});

export const isRetailSubCategoryDuplicateEntry = (
  items: any[],
  number: RetailSubCategoryLevel | "",
  value: string | null,
  excludeId?: string | null,
): boolean => {
  if (number === "" || !value?.trim()) {
    return false;
  }
  return items.some(
    (item: any) =>
      String(item.id) !== String(excludeId ?? "") &&
      Number(item[RETAIL_SUB_CATEGORY_FIELD_NUMBER]) === Number(number) &&
      String(getRetailSubCategoryNameFromRow(item) ?? "").trim() ===
        value.trim(),
  );
};

/** Display-only DND field key; not saved to payload. */
export const RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL = "Sub Category Level";

export const formatRetailSubCategoriesForDnd = (items: any[]): any[] =>
  items.map((item) => {
    const level = parseRetailSubCategoryLevelFromRow(item);
    const name = getRetailSubCategoryNameFromRow(item);
    return {
      ...item,
      [RETAIL_SUB_CATEGORY_FIELD_NAME]: name,
      [RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL]:
        level !== ""
          ? getRetailSubCategoryLevelLabel(level)
          : String(item[RETAIL_SUB_CATEGORY_FIELD_NUMBER] ?? ""),
    };
  });

export const stripRetailSubCategoryDndDisplayFields = (items: any[]): any[] =>
  items.map(({ [RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL]: _, ...rest }) => rest);

const isFlatRetailSubCategoryRow = (record: Record<string, unknown>): boolean =>
  RETAIL_SUB_CATEGORY_FIELD_NUMBER in record;

/** Build level 1–5 option lists from flat or legacy retail sub category rows. */
export const buildRetailSubCategoryOptionsByLevel = (
  rows: unknown[] | null | undefined,
): string[][] => {
  const buckets: string[][] = [[], [], [], [], []];
  const list = Array.isArray(rows) ? rows : [];

  for (const row of list) {
    if (!row || typeof row !== "object") {
      continue;
    }

    const record = row as Record<string, unknown>;
    if (!isRetailSubCategoryActive(record)) {
      continue;
    }

    if (isFlatRetailSubCategoryRow(record)) {
      const flatNum = record[RETAIL_SUB_CATEGORY_FIELD_NUMBER];
      const flatVal = getRetailSubCategoryNameFromRow(record);
      if (
        flatNum != null &&
        flatVal != null &&
        String(flatVal).trim() &&
        !isRetailSubCategorySampleValue(flatVal)
      ) {
        const idx = Number(flatNum) - 1;
        if (idx >= 0 && idx < 5) {
          buckets[idx].push(String(flatVal));
        }
      }
      continue;
    }

    RETAIL_SUB_CATEGORY_LEGACY_KEYS.forEach((key, index) => {
      const value = record[key];
      if (
        value != null &&
        String(value).trim() &&
        !isRetailSubCategorySampleValue(value)
      ) {
        buckets[index].push(String(value));
      }
    });
  }

  return buckets;
};
